/**
 * LoginContainer
 *
 * 책임:
 * - LoginScreen 렌더링
 * - 로그인 폼 제출 시 useAuth.login() 호출
 * - 로딩/에러 상태 표시
 * - 성공/실패 토스트 표시
 *
 * AdminApp에서 useAuth().isLoggedIn이 false일 때 렌더링
 */

import { useState, useContext } from 'react';
import { useAuth } from '../hooks/useAuth';
import { ToastContext } from '../context/ToastContext';
import { LoginScreen } from '../screens/Login/LoginScreen';

export function LoginContainer() {
  const { login } = useAuth();
  const toast = useContext(ToastContext);

  // 로딩 중 상태 (버튼 비활성화)
  const [loading, setLoading] = useState(false);
  // 에러 메시지 (폼 위에 표시)
  const [error, setError] = useState<string | null>(null);

  // LoginScreen의 onSubmit 콜백
  // LoginScreen은 자체 상태(id, pw)를 가지고 있으므로,
  // 여기서는 간단히 빈 callback을 전달하고,
  // 실제 로그인 로직은 LoginScreen 내부에서 처리하도록 함
  //
  // 더 나은 패턴: LoginScreen을 수정해서 id/pw를 callback으로 전달받도록 하면 됨
  // 현재는 LoginScreen이 자체 상태를 관리하므로, 여기서 훅을 통해 id/pw를 받지 못함
  //
  // 임시 해결: LoginScreen 내부의 onSubmit 콜백을 우리의 handleSubmit으로 연결
  // 하지만 LoginScreen의 onSubmit은 onLogin을 받으므로 수정 필요
  //
  // 최종 방안: LoginScreen을 수정하거나, LoginContainer에서 직접 LoginCard를 렌더링

  // 로그인 제출 핸들러
  // id/pw는 LoginScreen의 상태에서 가져옴 (LoginCard를 통해)
  const handleSubmit = async (id: string, password: string) => {
    setLoading(true);
    setError(null);
    try {
      // useAuth.login() 호출 → 토큰 저장 → Context 업데이트 → AdminApp 리렌더링
      await login(id, password);
      toast?.show('success', '로그인 성공');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      setError(message);
      toast?.show('error', message);
    } finally {
      setLoading(false);
    }
  };

  // 현재 LoginScreen은 onLogin prop을 받고, 자체 상태(id, pw)를 관리
  // 아래 코드는 LoginScreen 수정 후 작동
  // 임시: LoginScreen을 그대로 사용하면서, id/pw를 받는 방식으로 수정 필요

  // 현재 LoginScreen의 props 확인:
  // - onLogin: () => void (id/pw 전달 없음)
  //
  // 해결: LoginScreen을 수정하거나, 별도로 LoginCard를 직접 렌더링해야 함
  //
  // 실제 구현: LoginScreen 인터페이스를 수정해서 onSubmit(id, password)를 받도록 함

  return (
    <LoginScreen
      onSubmit={handleSubmit}
      loading={loading}
      error={error}
    />
  );
}
